import { Form } from "react-bootstrap";
import { useMemo, useState } from "react";
import { Button } from "components/core/basic/Button";
import { DateField, InputField, Label } from "components/custom/programs/fields";
import { useMutation } from "@tanstack/react-query";
import { ProductsTable } from "components/core/blocks/ProductsTable";
import api from "utils/api";
import { successToast, errorToast } from "utils/toasts";
import { useAppSelector } from "redux/hooks";
import useProducts from "hooks/useProducts";
import { useForm, useWatch } from "react-hook-form";
import { SelectField, CheckboxField } from "views/dsp/forms/fields";
import { AUDIENCE_LOOKALIKE_OPTIONS, getTemplateSpec, MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP } from "../utils";
import { useFetch } from "hooks/api";
import moment from "moment";
import Tags from "components/core/blocks/Tags";
import { Flex } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { useMediaQuery } from "usehooks-ts";
import { useAudienceTemplates } from "./useAudienceTemplates";

const INITIAL_AUDIENCE_FORM_DATA = {
  audience_name: "",
  description: "",
  tags: [],
  refresh_rate_days: 7,
  time_window_relative: true,
  time_window_start: null,
  time_window_end: new Date(moment().format("YYYY-MM-DD")),
  lookalike_audience_expected_reach: AUDIENCE_LOOKALIKE_OPTIONS[0].value,
  views_range_lower: null,
  views_range_upper: null,
  search_term_fields: [],
};

const EMPTY_ARRAY = [];

const suggestedTags = ["Awareness", "Consideration", "Purchase Intent", "Loyalty"];

const validateRequiredFields = (formState, asins, templateSpec) => {
  const {
    audience_name,
    time_window_start,
    time_window_end,
    template_name,
    advertiser_id,
    search_term_fields,
    data_set_id,
  } = formState;

  if (
    !audience_name ||
    !time_window_start ||
    !time_window_end ||
    !template_name ||
    (!asins?.length && templateSpec?.required_fields?.includes('["asins"]')) ||
    (!search_term_fields?.length && templateSpec?.required_fields?.includes('["search_terms"]')) ||
    (!data_set_id && templateSpec?.required_fields?.includes('["datalinks"]')) ||
    !advertiser_id
  ) {
    return false;
  }

  return true;
};

const AudienceForm = ({ setShowAudienceForm, defaultFormData = {}, readOnly = false }) => {
  const [selectedAsins, setSelectedAsins] = useState(defaultFormData?.asins ? defaultFormData.asins : []);

  const channel = useAppSelector((state) => state.channel);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const mobileStyle = useMediaQuery("(max-width:1000px)");
  const { templates } = useAudienceTemplates();
  const templateOptions = useMemo(() => {
    return (
      templates?.map((audienceTemplate) => ({
        value: audienceTemplate.audience_template_name,
        label: audienceTemplate.audience_display_name,
      })) ?? []
    );
  }, [templates]);

  const productReportFilters = {
    mp: marketPlace,
    channel: channel.currentChannel,
    date_group: "day",
    empty: true,
    group_by: "product_id",
    start_date: moment().subtract(31, "days").format("YYYY-MM-DD"),
    end_date: moment().subtract(1, "days").format("YYYY-MM-DD"),
    asins: selectedAsins?.filter((asin) => asin.length === 10 && asin.toLowerCase().startsWith("b0"))?.join(","),
  };

  const { productData, isLoading: isProductDataLoading } = useProducts(
    true,
    null,
    null,
    productReportFilters,
    !!selectedAsins?.length &&
      selectedAsins?.filter((asin) => asin.length === 10 && asin.toLowerCase().startsWith("b0"))?.length > 0,
  );

  const errorClassNames = "d-block ms-3 mt-0 fs-5";

  const {
    control,
    reset,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: { ...INITIAL_AUDIENCE_FORM_DATA, ...defaultFormData },
  });

  const formState = useWatch({ control });

  const templateSpec = useMemo(() => {
    return getTemplateSpec(templates, formState.template_name);
  }, [formState.template_name, templates]);

  const isFieldVisible = (fieldName) => {
    return (
      templateSpec?.optional_fields?.includes(`["${fieldName}"]`) ||
      templateSpec?.required_fields?.includes(`["${fieldName}"]`)
    );
  };

  const isFieldOptional = (fieldName) => {
    return !templateSpec?.required_fields?.includes(`["${fieldName}"]`);
  };

  // Eventually ASIN selector will render based on audience template fields spec from API
  const showAsinsField = isFieldVisible("asins");
  const asinsFieldOptional = isFieldOptional("asins");
  const showViewsRangeFields = isFieldVisible("conversion_range");
  const showSearchTermFields = isFieldVisible("search_terms");
  const showDataLinkFields = isFieldVisible("datalinks");

  const { organization_id: orgId } = useAppSelector((state) => state.user);
  const { data: dataLinks } = useFetch(
    ["datalinks", orgId],
    "/amc/datalink/",
    {},
    {
      enabled: showDataLinkFields,
      select: (res) => res.data.results,
    },
  );

  const dataLinksWithStatus = useMemo(() => {
    if (!dataLinks) {
      return [];
    }
    return dataLinks?.map((dataLink) => {
      let currentUploadStatus = dataLink.upload_status?.[0]?.upload_status;
      if (dataLink.shopify_org) {
        currentUploadStatus =
          dataLink.upload_status.find((status) => status.upload_status === "SUCCESS") || currentUploadStatus;
      }
      return {
        ...dataLink,
        currentUploadStatus,
      };
    });
  }, [dataLinks]);

  if (!showViewsRangeFields && (formState.views_range_lower || formState.views_range_upper)) {
    setValue("views_range_lower", null);
    setValue("views_range_upper", null);
  }

  if (!showSearchTermFields && formState.search_term_fields?.length) {
    setValue("search_term_fields", []);
  }

  if (moment(formState.time_window_start).isAfter(moment(formState.time_window_end)) && !errors.time_window_start) {
    setError("time_window_start", {
      type: "custom",
      message: `Start date must be before ${moment(formState.time_window_end).format("MMMM D")}`,
    });
  }

  if (errors.time_window_start && moment(formState.time_window_start).isBefore(moment(formState.time_window_end))) {
    clearErrors("time_window_start");
  }
  // Ensure minimum refresh rate
  if (
    formState.refresh_rate_days > 0 &&
    formState.refresh_rate_days < 7 &&
    formState?.lookalike_audience_expected_reach &&
    !errors.refresh_rate_days
  ) {
    setError("refresh_rate_days", {
      type: "custom",
      message: "Refresh range days must be at least 7",
    });
  }
  if (errors.refresh_rate_days && (formState?.refresh_rate_days >= 7 || !formState.lookalike_audience_expected_reach)) {
    clearErrors("refresh_rate_days");
  }
  if (
    selectedAsins?.length > 0 &&
    !selectedAsins?.every((asin) => asin.length === 10 && asin.toLowerCase().startsWith("b0")) &&
    !errors.asins
  ) {
    setError("asins", {
      type: "custom",
      message: "Invalid ASIN detected. All ASINs must start with B0 and be 10 characters in length",
    });
  }

  if (errors.asins && selectedAsins?.every((asin) => asin.length === 10 && asin.toLowerCase().startsWith("b0"))) {
    clearErrors("asins");
  }

  const { data: amcInstances } = useFetch(
    ["amcInstances"],
    "/amc/instances/",
    {},
    {
      select: (d) => d.data,
      default: [],
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const { data: amcAccounts } = useFetch(
    ["amcAccounts"],
    "/amc/onboarding/accounts",
    {
      instance_id: amcInstances?.[0]?.instance_id,
    },
    {
      select: (d) => d.data?.advertisers,
      default: [],
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!amcInstances?.length,
    },
  );
  const amcAccountOptions = useMemo(() => {
    if (!amcAccounts) {
      return [];
    }

    return amcAccounts?.map((account) => {
      const { name, type, id, marketplaceId } = account;
      let formattedAdTypes = "";
      const countryCode = MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP[marketplaceId];

      if (id.includes("ENTITY")) {
        formattedAdTypes = "Sponsored Product, Sponsored Brand and Sponsored Display";
      }

      if (type === "DISPLAY") {
        formattedAdTypes = "DSP";
      }
      return {
        value: id,
        label: `${name} - ${formattedAdTypes} (${id})${countryCode ? " (" + countryCode + ")" : ""}`,
      };
    });
  }, [amcAccounts]);

  const createCustomAudience = useMutation({
    mutationFn: async () => {
      const {
        audience_name,
        description,
        tags,
        refresh_rate_days,
        time_window_start,
        time_window_end,
        time_window_relative,
        lookalike_audience_expected_reach,
        template_name,
        advertiser_id,
        views_range_lower,
        views_range_upper,
        search_term_fields,
      } = formState ?? {};
      const asins = [
        ...new Set(
          selectedAsins.map((productId) => {
            return productData?.find((product) => product.asin === productId)?.asin;
          }),
        ),
      ];

      const amcInstanceId = amcInstances?.[0]?.id;
      const createAudienceData = {
        spec: {
          audience_name,
          description,
          refresh_rate_days,
          time_window_start,
          time_window_end,
          time_window_relative,
          lookalike_audience_expected_reach,
          advertiser_id: advertiser_id,
        },
        sql_template: [
          {
            template_name,
          },
        ],
        amc_instance: amcInstanceId,
        tags: tags,
      };
      if (showDataLinkFields && formState.data_set_id) {
        createAudienceData.sql_template[0]["table_name"] = formState.data_set_id;
      }

      if (showViewsRangeFields && (views_range_upper || views_range_lower)) {
        createAudienceData.sql_template[0]["conversion_lower_bound"] = views_range_lower;
        createAudienceData.sql_template[0]["conversion_upper_bound"] = views_range_upper;
      }

      if (showSearchTermFields && search_term_fields?.length) {
        createAudienceData.sql_template[0]["search_terms"] = search_term_fields;
      }
      if (showAsinsField && asins.length) {
        createAudienceData.sql_template[0]["asins"] = asins;
      }

      return await api.post("/amc/audiencespec/", createAudienceData);
    },

    onSuccess: () => {
      successToast({
        message: "New audience request successfully submitted",
      });
      setShowAudienceForm(false);
      reset();
    },

    onError: (error) => {
      errorToast({ message: "Failed to send new audience request", error });
    },
  });

  return (
    <div className="pb-5 custom-scroll-container" style={{ maxHeight: "78vh", overflowY: "auto" }}>
      {!readOnly && (
        <>
          <h2 className="fw-bold fs-1 ps-2 text-center mt-0">Create a New AMC Audience</h2>
          <p className="text-center fs-4 w-75 mx-auto">
            Complete the fields below to create a new audience. Once submitted, it may takes up to 24 hours for your
            audience to become available through Amazon.
          </p>
        </>
      )}
      <form onSubmit={(e) => e.preventDefault()} className="audience-creation-modal-form">
        <Flex
          direction={"column"}
          gap={"large"}
          className={css({
            width: mobileStyle ? "95%" : "75%",
            margin: "auto",
          })}
        >
          <InputField
            label="Audience Name"
            fieldName="audience_name"
            errors={errors}
            control={control}
            errorClassNames={errorClassNames}
            disabled={readOnly}
            rules={{
              required: { value: true, message: "Audience name is required" },
            }}
          />
          <InputField
            label="Description"
            fieldName="description"
            errors={errors}
            control={control}
            disabled={readOnly}
            errorClassNames={errorClassNames}
          />
          <Tags
            disabled={readOnly}
            name={
              <span
                style={{
                  fontSize: "1.5rem",
                  position: "relative",
                }}
                className="pb-3 d-block fw-bold"
              >
                Tags (optional)
              </span>
            }
            style={{ position: "relative", zIndex: 3000 }}
            filledTags={formState.tags}
            saveFilledTags={(tags) => setValue("tags", tags)}
            showSuggestionsOnFocus={true}
            placeholder={readOnly ? readOnly : "Add tags here..."}
            hideEditButton={true}
            reviewDisplay={false}
            suggestedTags={suggestedTags}
            showSuggestedTags={!readOnly}
            allowNew={false}
          />
          <SelectField
            label={"Advertiser"}
            options={amcAccountOptions}
            fieldName={"advertiser_id"}
            control={control}
            disabled={readOnly}
          />

          <div
            className={css({
              display: "grid",
              gridTemplateColumns: mobileStyle ? "repeat(1fr)" : "repeat(3, 1fr)",
              gap: "40px",
            })}
          >
            <DateField
              label={"Start Date"}
              fieldName={"time_window_start"}
              control={control}
              errors={errors}
              disabled={readOnly}
              rules={{
                required: { value: true, message: "A start date is required" },
              }}
              errorClassNames={errorClassNames}
              maxDate={new Date(moment().format("YYYY-MM-DD"))}
            />
            <DateField
              label={"End Date"}
              fieldName={"time_window_end"}
              control={control}
              errors={errors}
              disabled={readOnly}
              rules={{
                required: { value: true, message: "A start date is required" },
              }}
              errorClassNames={errorClassNames}
              maxDate={new Date(moment().format("YYYY-MM-DD"))}
            />
            <InputField
              label="Refresh Rate Days"
              fieldName="refresh_rate_days"
              type={"number"}
              errors={errors}
              control={control}
              errorClassNames={errorClassNames}
              disabled={readOnly}
              rules={{
                required: { value: true, message: "A refresh rate is required" },
                min: {
                  value: 0,
                  message: `Refresh rate days must be at least 0`,
                },
                max: {
                  value: 21,
                  message: `Refresh rate days cannot be greater than 21 `,
                },
              }}
            />
          </div>
          <CheckboxField
            label={"Time Window Relative"}
            fieldName="time_window_relative"
            control={control}
            disabled={readOnly}
          />
          <SelectField
            label={"Template"}
            options={templateOptions}
            fieldName={"template_name"}
            control={control}
            disabled={readOnly}
          />
          <SelectField
            label={"Lookalike Audience"}
            options={AUDIENCE_LOOKALIKE_OPTIONS}
            fieldName={"lookalike_audience_expected_reach"}
            control={control}
            disabled={readOnly}
          />

          {showDataLinkFields && (
            <>
              <SelectField
                label={"1st Party Data Source"}
                disabled={readOnly}
                options={dataLinksWithStatus
                  ?.filter(
                    (dataLink) =>
                      dataLink?.currentUploadStatus && dataLink?.currentUploadStatus?.upload_status !== "FAILED",
                  )
                  ?.map((dataLink) => {
                    const statusStr =
                      (
                        dataLink?.currentUploadStatus?.upload_status === "SUCCESS" ||
                        dataLink?.currentUploadStatus === "SUCCESS"
                      ) ?
                        ""
                      : " (In Progress)";

                    return {
                      value: dataLink.data_set_id,
                      label:
                        dataLink?.data_source === "USER_UPLOAD" ?
                          `${dataLink.name || "N/A"}${statusStr}`
                        : `${dataLink.shopify_org?.amazon_profile_name}${statusStr}`,
                      disabled:
                        dataLink?.currentUploadStatus !== "SUCCESS" &&
                        dataLink?.currentUploadStatus?.upload_status !== "SUCCESS",
                    };
                  })}
                fieldName={"data_set_id"}
                control={control}
              />
              {!dataLinksWithStatus.find((dataLink) => dataLink?.currentUploadStatus?.upload_status === "SUCCESS") && (
                <small className="d-block pt-3 text-danger">No data sources available</small>
              )}
            </>
          )}

          {showViewsRangeFields ?
            <Flex gap={"medium"}>
              <InputField
                label="Views Range Low"
                fieldName="views_range_lower"
                type={"number"}
                errors={errors}
                control={control}
                errorClassNames={errorClassNames}
                disabled={readOnly}
              />
              <InputField
                label="Views Range High"
                fieldName="views_range_upper"
                type={"number"}
                errors={errors}
                control={control}
                errorClassNames={errorClassNames}
                disabled={readOnly}
              />
            </Flex>
          : null}

          {showSearchTermFields ?
            <Tags
              name={
                <span
                  style={{
                    fontSize: "1.5rem",
                    position: "relative",
                  }}
                  className="pb-3 d-block fw-bold"
                >
                  Search Term Fields
                </span>
              }
              style={{ position: "relative", zIndex: 3000 }}
              filledTags={formState.search_term_fields}
              saveFilledTags={(tags) => setValue("search_term_fields", tags)}
              placeholder={"Add search terms..."}
              hideEditButton={false}
              reviewDisplay={false}
              disabled={readOnly}
            />
          : null}
          {showAsinsField ?
            <Form.Group className="py-4">
              <Label>ASINs {asinsFieldOptional ? "(optional)" : null}</Label>
              <div style={{ overflowY: "auto" }}>
                <Tags
                  filledTags={selectedAsins}
                  saveFilledTags={(asins) => setSelectedAsins(asins)}
                  placeholder={readOnly ? readOnly : "Enter ASINs..."}
                  className="pb-4"
                  name={readOnly ? readOnly : "Add Audience ASINs"}
                  disabled={readOnly}
                  hideEditButton={readOnly}
                />
              </div>
              {errors.asins ?
                <small className={`${errorClassNames} pt-3 text-danger`}>{errors.asins.message}</small>
              : null}
              {selectedAsins?.filter((asin) => asin.length === 10 && asin.toLowerCase().startsWith("b0"))?.length ?
                <>
                  <small className="d-block pt-4">* Showing product performance over the past 30 days</small>
                  <ProductsTable
                    selectedProducts={[]}
                    marketPlace={marketPlace}
                    productData={productData ?? EMPTY_ARRAY}
                    isLoading={isProductDataLoading}
                    hideBulkActionsButton={false}
                    titleColWidth={350}
                    minRows={0}
                    isSelectable={false}
                    customColumns={[
                      {
                        key: "sales",
                        compareKey: "sales_percent",
                        options: { width: 85 },
                      },
                      {
                        key: "units",
                        compareKey: "units_percent",
                        options: { width: 85 },
                      },
                      {
                        key: "ad_sales",
                        compareKey: "ad_sales_percent",
                        options: { width: 85 },
                      },
                      {
                        key: "ad_spend",
                        compareKey: "ad_spend_percent",
                        options: { width: 85 },
                      },
                    ]}
                    bulkActionOptions={[]}
                    hideCompareValue={true}
                    ExpandedContent={null}
                    isSaving={false}
                    isExpandable={false}
                  />
                </>
              : null}
            </Form.Group>
          : null}
        </Flex>
      </form>
      {!readOnly && (
        <Flex justifyContent={"center"} gap="large" className="pt-5">
          <Button variant={"secondary"} onPress={() => setShowAudienceForm(false)}>
            Cancel
          </Button>
          <Button
            disabled={Object.keys(errors)?.length || !validateRequiredFields(formState, selectedAsins, templateSpec)}
            isPending={createCustomAudience.isPending}
            onPress={() => createCustomAudience.mutate()}
          >
            Create
          </Button>
        </Flex>
      )}
    </div>
  );
};

export default AudienceForm;
